/**
 * executionAdapters/importAdapter.ts
 *
 * Execution adapter for the COPY_IN / MOVE_IN workflows.
 * Wraps: copy/move → rename → BL create.
 *
 * NOTE: The actual copy/move step currently delegates to the existing
 * mover/processNode flow which handles conflicts. This adapter is a
 * simplified version for the new workflow architecture.
 * Full migration of COPY_IN/MOVE_IN will be done in Step 8.
 */

import { api } from '../../../../general/apiScenarios';
import type { PreparedFile, CreativeBLSettings, FileExecutionResult } from '../contracts';

export type ImportMode = 'COPY' | 'MOVE';

/**
 * Copy or move an existing file to the target folder, rename it, and create the BL entity.
 */
export async function importAndAttach(
  file: PreparedFile,
  targetNodeId: number,
  context: CreativeBLSettings,
  mode: ImportMode,
  signal: AbortSignal,
): Promise<FileExecutionResult> {
  if (!file.nodeRef) {
    return { success: false, fileId: file.id, error: 'No node reference' };
  }

  const sourceNodeId = file.nodeRef.id;

  // Step 1: Copy or Move
  const copyMoveResult =
    mode === 'COPY'
      ? await api.fs.copyNodeAsync({
          node_id: sourceNodeId,
          new_parent_node_id: targetNodeId,
          overwrite: false,
        })
      : await api.fs.moveNode({
          node_id: sourceNodeId,
          new_parent_node_id: targetNodeId,
          overwrite: false,
        });

  if (signal.aborted) return { success: false, fileId: file.id, error: 'Aborted' };

  if (!copyMoveResult.success) {
    return { success: false, fileId: file.id, error: `${mode} failed` };
  }

  const newNodeId: number =
    'node' in copyMoveResult.data.response
      ? copyMoveResult.data.response.node.id
      : copyMoveResult.data.response.id;

  // Step 2: Rename if needed
  if (file.originalName !== file.newFilename) {
    const renameResult = await api.fs.rename(file.newFilename, newNodeId);
    if (signal.aborted) return { success: false, fileId: file.id, error: 'Aborted' };

    if (!renameResult.success) {
      return { success: false, fileId: file.id, error: 'Rename failed' };
    }
  }

  // Step 3: Create BL entity
  const blResult = await api.bl.blCreateCreativeFile(newNodeId, context.creative.id, file.meta);
  if (signal.aborted) return { success: false, fileId: file.id, error: 'Aborted' };

  if (!blResult.success) {
    return { success: false, fileId: file.id, error: 'BL entity creation failed' };
  }

  return { success: true, nodeId: newNodeId };
}
